/**
 * Lift2Fit — coaching workspace: connect requests.
 *
 * The coach's inbox for LIFTER-INITIATED connect requests (migration 0025).
 * The lifter asks from the coach's public profile; the coach answers here with
 * an optional note. Accepting creates the `trainee_links` row the rest of the
 * system already understands — the roster, sharing, packs and chat all follow
 * from it — and posts the note as the first chat message. Declining stores the
 * note on the request, where the lifter reads it, so a decline is never silent.
 *
 * This lives at the TOP of the Clients tab, the surface a coach actually opens:
 * a request is the first thing that becomes a client, so it belongs before the
 * roster, not hidden behind a menu.
 */
import { useState } from 'react';
import { api, type CoachInvite } from '@/lib/api';
import { Button } from '@/components/ui';
import { AlertIcon, ArrowRightIcon, CheckIcon, CloseIcon } from '@/lib/icons';
import { formatDate, timeAgo } from '@/lib/format';
import { LiveMessage, Panel, StatusChip } from '../components';

type Tone = 'info' | 'success' | 'danger';

function requesterName(invite: CoachInvite): string {
  return invite.inviter?.displayName ?? invite.trainee?.displayName ?? 'A lifter';
}

function requesterEmail(invite: CoachInvite): string {
  return invite.inviter?.email ?? invite.trainee?.email ?? '';
}

/**
 * One pending request: who asked, when, and the coach's answer with its optional
 * note. The note is shared by both actions — the coach writes it once and then
 * chooses accept or decline, so the decision is never a bare button.
 */
function PendingRequest({
  invite,
  busy,
  onDecide,
}: {
  invite: CoachInvite;
  busy: boolean;
  onDecide: (invite: CoachInvite, action: 'accept' | 'decline', message: string) => void;
}) {
  const [message, setMessage] = useState('');
  const email = requesterEmail(invite);

  return (
    <li
      data-testid={`connect-request-${invite.id}`}
      className="flex flex-col gap-3 border-l-2 border-l-accent bg-page-subtle px-3 py-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <p className="flex flex-wrap items-center gap-2 text-[15px] font-bold text-ink">
            {requesterName(invite)}
            <StatusChip tone="info" icon={<ArrowRightIcon />}>
              Wants to connect
            </StatusChip>
          </p>
          <p className="text-[12px] text-ink-muted">
            {email || 'no email on file'} · asked {timeAgo(invite.createdAt)} (
            {formatDate(invite.createdAt)}) · expires {formatDate(invite.expiresAt)}
          </p>
          <p className="text-[12px] text-ink-muted">
            Would share {invite.sharedFields.join(', ') || 'nothing'} once linked.
          </p>
        </div>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
          Message (optional)
        </span>
        <textarea
          data-testid={`connect-request-message-${invite.id}`}
          value={message}
          maxLength={2000}
          rows={2}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Say hello, or explain why you can't take them on right now."
          className="border border-hairline bg-page px-3 py-2 text-[14px] text-ink"
        />
      </label>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={busy}
          data-testid={`connect-request-accept-${invite.id}`}
          onClick={() => onDecide(invite, 'accept', message)}
        >
          <CheckIcon />
          {busy ? 'Working…' : 'Accept & connect'}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          data-testid={`connect-request-decline-${invite.id}`}
          onClick={() => onDecide(invite, 'decline', message)}
        >
          <CloseIcon />
          Decline
        </Button>
      </div>
    </li>
  );
}

/** A request the coach already answered, with the note they left. */
function DecidedRequest({ invite }: { invite: CoachInvite }) {
  const accepted = invite.status === 'accepted';
  return (
    <li
      data-testid={`connect-request-decided-${invite.id}`}
      className="flex flex-col gap-1 border border-hairline px-3 py-2"
    >
      <p className="flex flex-wrap items-center gap-2 text-[14px] font-bold text-ink">
        {requesterName(invite)}
        <StatusChip tone={accepted ? 'success' : 'neutral'}>
          {accepted ? 'Connected' : 'Declined'}
        </StatusChip>
      </p>
      <p className="text-[12px] text-ink-muted">
        {invite.decidedAt ? `Answered ${timeAgo(invite.decidedAt)}` : 'Answered'}
      </p>
      {invite.decisionMessage ? (
        <p className="text-[13px] text-ink" data-testid={`connect-request-note-${invite.id}`}>
          “{invite.decisionMessage}”
        </p>
      ) : null}
    </li>
  );
}

export function ConnectRequestsPanel({
  requests,
  pending,
  onChanged,
}: {
  requests: CoachInvite[];
  pending: number;
  onChanged: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tone, setTone] = useState<Tone>('info');

  const open = requests.filter((invite) => invite.status === 'pending');
  const decided = requests.filter((invite) => invite.status !== 'pending').slice(0, 5);

  const decide = async (invite: CoachInvite, action: 'accept' | 'decline', message: string) => {
    setBusyId(invite.id);
    setTone('info');
    setNotice(action === 'accept' ? 'Connecting…' : 'Declining…');
    const trimmed = message.trim();
    const result =
      action === 'accept'
        ? await api.acceptCoachInvite(invite.code, undefined, trimmed)
        : await api.declineCoachInvite(invite.code, trimmed);
    setBusyId(null);
    if (result.problem || !result.data) {
      setTone('danger');
      setNotice(result.problem?.detail ?? 'That request could not be answered.');
      return;
    }
    setTone('success');
    setNotice(
      action === 'accept'
        ? `${requesterName(invite)} is connected. They appear on the roster below; the link starts with ${invite.sharedFields.join(', ') || 'no fields'} shared.`
        : `${requesterName(invite)} was declined${trimmed !== '' ? ' and your message is on their request' : ''}.`,
    );
    onChanged();
  };

  return (
    <Panel
      testId="crm-requests"
      title="Connect requests"
      subtitle="Lifters who asked to connect with you. Accept to add them to your roster, or decline with a note they will read."
      actions={
        pending > 0 ? (
          <span data-testid="crm-requests-count">
            <StatusChip tone="info" icon={<AlertIcon />}>
              {pending} waiting
            </StatusChip>
          </span>
        ) : (
          <StatusChip tone="success" icon={<CheckIcon />}>
            All answered
          </StatusChip>
        )
      }
      bodyClassName="gap-3"
    >
      {open.length === 0 && decided.length === 0 ? (
        <p className="text-[13px] text-ink-muted" data-testid="crm-requests-empty">
          No connect requests yet. A lifter asks from your public profile; their request appears here the moment they
          send it.
        </p>
      ) : null}
      {open.length > 0 ? (
        <ul className="flex flex-col gap-3" data-testid="crm-requests-pending">
          {open.map((invite) => (
            <PendingRequest key={invite.id} invite={invite} busy={busyId === invite.id} onDecide={decide} />
          ))}
        </ul>
      ) : null}
      {decided.length > 0 ? (
        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Recently answered</span>
          <ul className="flex flex-col gap-2" data-testid="crm-requests-decided">
            {decided.map((invite) => (
              <DecidedRequest key={invite.id} invite={invite} />
            ))}
          </ul>
        </div>
      ) : null}
      <LiveMessage message={notice} tone={tone} />
    </Panel>
  );
}
